/**
 * Adding a service by hand, one flow for every place that offers it: the project Home's Apps "+" and the service
 * dropdown's "+ Create new service". The ways are the same: a new empty service, a .zip, a folder, a git repository,
 * a container image (ImageServiceDialog: RabbitMQ, OpenSearch, … run with Docker).
 *
 * `useAddService(projectId, { taken, onDone, onCreateEmpty })` holds it all: `{ state, busy, add, dialogs, importFrom,
 * openGit }` (the last two for a page that also takes a drop, or has its own "Start from git").
 * `add(kind, collected)` takes what the menu or the chooser picked ('new', 'git', or 'import' with the collected
 * .zip/folder); `state` is the import's `{ phase, text }` (or null); `dialogs` is the git dialog and the empty-service
 * form, rendered once by the caller. `onDone(info)` runs after a service was added, whichever the way (info.services:
 * the `services/<name>` paths when known): the caller refreshes its services and shows the new one. `onCreateEmpty`
 * (optional) replaces the default for an empty service, which only registers it.
 *
 * `AddServiceMenu` is the Home's dropdown (a trigger button inside); `AddServiceChooser` is the same four ways as a
 * dialog, for a row that has no room for a menu. Both link "What can Flow run?" (Supported languages and frameworks).
 */
import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Dropdown } from 'antd';
import { Cube, Folder, FolderOpen, GitBranch, FileZip, Plus } from '@phosphor-icons/react';
import CreateServiceModal from '../CreateServiceModal';
import { StartFromGitDialog } from '../sandbox/StartFromGit';
import { useImportIntoProject } from './StartBy';
import { collectFromFileList } from '../../utils/treeUpload/collectEntries';
import { ARCHIVE_ACCEPT } from '../../utils/treeUpload/readArchive';
import * as vfsClient from '../../utils/vfsClient';
import SupportedStacksLink from '../stacks/SupportedStacksLink';
import ImageServiceDialog from '../apps/ImageServiceDialog';
import './AddService.css';

export const ADD_ITEMS = [
  { key: 'new', label: 'New empty service…', hint: 'A folder to write the code in' },
  { key: 'zip', label: 'From a .zip…', hint: 'Each top folder with its own package becomes a service' },
  { key: 'folder', label: 'From a folder…', hint: 'Pick a folder from your computer' },
  { key: 'git', label: 'From a git repository…', hint: 'Clone one or more repositories' },
  { key: 'image', label: 'From a container image…', hint: 'RabbitMQ, OpenSearch, NATS, MySQL… run with Docker' },
];
// The ways that open a dialog of their own (not a file picker).
const DIALOG_WAYS = ['git', 'new', 'image'];

export function useAddService(projectId, { taken = [], onDone = null, onCreateEmpty = null } = {}) {
  const [gitOpen, setGitOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const imported = useCallback((result) => { onDone?.({ services: result?.services || [] }); }, [onDone]);
  const projectImport = useImportIntoProject(projectId, imported, { taken });
  const cloned = useCallback((id, subfolder) => { onDone?.({ services: subfolder ? [`services/${subfolder}`] : [] }); }, [onDone]);
  const add = (kind, collected) => {
    if (kind === 'git') { setGitOpen(true); return; }
    if (kind === 'new') { setNewOpen(true); return; }
    if (kind === 'image') { setImageOpen(true); return; }
    projectImport.importFrom(collected);
  };
  const created = (serviceData, files) => {
    if (onCreateEmpty) { onCreateEmpty(serviceData, files); } else {
      vfsClient.registerVfsService(projectId, serviceData.name);
      onDone?.({ services: [serviceData.name] });
    }
  };
  const dialogs = projectId ? (
    <>
      <StartFromGitDialog onCloned={cloned} onClose={() => setGitOpen(false)} open={gitOpen} projectId={projectId} takenNames={taken} />
      <CreateServiceModal isOpen={newOpen} onClose={() => setNewOpen(false)} onCreate={created} projectId={projectId} takenNames={taken} />
      <ImageServiceDialog onAdded={(made) => onDone?.({ services: [made.service] })} onClose={() => setImageOpen(false)} open={imageOpen} projectId={projectId} taken={taken} />
    </>
  ) : null;
  return { state: projectImport.state, busy: projectImport.busy, importFrom: projectImport.importFrom, openGit: () => setGitOpen(true), add, dialogs };
}

/** The two hidden pickers a .zip or a folder comes from; `pick(key)` opens one. */
function usePickers(onAdd, after = null) {
  const zipRef = useRef(null);
  const folderRef = useRef(null);
  const picked = (event) => {
    const collected = collectFromFileList(event.target.files);
    event.target.value = '';
    onAdd('import', collected);
    after?.();
  };
  const pick = (key) => (key === 'zip' ? zipRef : folderRef).current?.click();
  const inputs = (
    <>
      <input accept={ARCHIVE_ACCEPT} data-testid="add-service-zip" hidden onChange={picked} ref={zipRef} type="file" />
      <input data-testid="add-service-folder" hidden multiple onChange={picked} ref={folderRef} type="file" webkitdirectory="" />
    </>
  );
  return { pick, inputs };
}

/** The Apps "+": a menu of the ways; `children` is the trigger button. */
export function AddServiceMenu({ onAdd, children }) {
  const { pick, inputs } = usePickers(onAdd);
  // Last, apart: what Flow can run (Supported languages and frameworks, in a dialog over this one).
  const items = [...ADD_ITEMS.map(({ key, label }) => ({ key, label })), { type: 'divider' }, { key: 'stacks', label: <SupportedStacksLink /> }];
  const onClick = ({ key }) => {
    if (key === 'stacks') { return; }
    if (DIALOG_WAYS.includes(key)) { onAdd(key); return; }
    pick(key);
  };
  return (
    <>
      <Dropdown menu={{ items, onClick }} placement="bottomRight" trigger={['click']}>{children}</Dropdown>
      {inputs}
    </>
  );
}

AddServiceMenu.propTypes = { children: PropTypes.node.isRequired, onAdd: PropTypes.func.isRequired };

const ICONS = { new: Plus, zip: FileZip, folder: FolderOpen, git: GitBranch, image: Cube };

/** The same ways as a dialog (the service dropdown's "+ Create new service"). Closes once one is picked. */
export function AddServiceChooser({ open, onClose, onAdd }) {
  const { pick, inputs } = usePickers(onAdd, onClose);
  if (!open) { return null; }
  const choose = (key) => {
    if (DIALOG_WAYS.includes(key)) { onClose(); onAdd(key); return; }
    pick(key);
  };
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        aria-label="Add a service"
        aria-modal="true"
        className="modal-content add-service-chooser"
        data-testid="add-service-chooser"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => { if (event.key === 'Escape') { onClose(); } }}
        role="dialog"
      >
        <div className="add-service-chooser-head">
          <h2>Add a service</h2>
          <button aria-label="Close" className="add-service-chooser-close" onClick={onClose} type="button">×</button>
        </div>
        <ul className="add-service-chooser-list">
          {ADD_ITEMS.map(({ key, label, hint }, index) => {
            const Glyph = ICONS[key] || Folder;
            return (
              <li key={key}>
                <button autoFocus={index === 0} className="add-service-chooser-way" data-testid={`add-service-way-${key}`} onClick={() => choose(key)} type="button">
                  <Glyph aria-hidden size={20} />
                  <span><strong>{label}</strong><small>{hint}</small></span>
                </button>
              </li>
            );
          })}
        </ul>
        <p className="add-service-chooser-foot"><SupportedStacksLink /></p>
      </div>
      {inputs}
    </div>,
    document.body,
  );
}

AddServiceChooser.propTypes = { onAdd: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired, open: PropTypes.bool.isRequired };
